import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import StudentExerciseProtection from '../../components/StudentExerciseProtection';
import { studentAPI } from '../../services/api';
import './QuizPage.css';

function QuizPage({ user, onLogout }) {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const [quizStatus, setQuizStatus] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);

  useEffect(() => {
    loadQuizStatus();
  }, [exerciseId]);

  // Auto-save answers
  useEffect(() => {
    if (attempt && Object.keys(answers).length > 0) {
      if (autoSaveTimer) clearTimeout(autoSaveTimer);
      
      const timer = setTimeout(() => {
        saveAnswers();
      }, 2000);
      
      setAutoSaveTimer(timer);
      
      return () => clearTimeout(timer);
    }
  }, [answers]);

  const loadQuizStatus = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getQuizStatus(exerciseId);
      setQuizStatus(response.data);
      
      if (response.data.inProgressAttempt) {
        // Load in-progress attempt
        const attemptRes = await studentAPI.getQuizAttempt(response.data.inProgressAttempt.id);
        setAttempt(attemptRes.data);
        setAnswers(attemptRes.data.answers || {});
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load quiz status');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.startQuiz(exerciseId);
      setAttempt(response.data);
      setAnswers({});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start quiz');
    } finally {
      setLoading(false);
    }
  };

  const saveAnswers = async () => {
    if (!attempt) return;
    
    try {
      await studentAPI.saveQuizAnswers(attempt.attemptId, answers);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  const submitQuiz = async () => {
    if (!attempt) return;

    try {
      setSubmitting(true);
      await studentAPI.saveQuizAnswers(attempt.attemptId, answers);
      await studentAPI.submitQuiz(attempt.attemptId);
      navigate(`/student/exercises/${exerciseId}/results`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmSubmitQuiz = async () => {
    await submitQuiz();
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading quiz...</div>
      </Layout>
    );
  }

  if (error && !quizStatus) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  if (quizStatus?.quizPassed) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="quiz-page">
          <div className="card">
            <h1>Quiz already passed</h1>
            <p>You have completed this exercise and passed its quiz. You can review your answers, but another attempt is not available.</p>
            <div className="quiz-completed-actions">
              {quizStatus.reviewAvailable && (
                <Link to={`/student/exercises/${exerciseId}/results`} className="btn btn-primary">
                  Review Quiz Results
                </Link>
              )}
              <Link to="/student/dashboard" className="btn btn-secondary">
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Quiz not available yet
  if (quizStatus && !quizStatus.canTakeQuiz) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="quiz-page">
          <h1>Quiz Not Available</h1>
          <div className="card">
            <p>You must complete all learning tasks before taking the quiz.</p>
            <Link to={`/student/exercises/${exerciseId}`} className="btn btn-primary">
              Continue Learning Tasks
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Show quiz start page
  if (!attempt) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="quiz-page">
          <h1>Quiz</h1>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="card">
            <h2>Ready to start the quiz?</h2>
            <p>You will be given {quizStatus.questionCount || 10} questions.</p>
            <p>Passing score: {quizStatus.passingScore}%</p>
            
            {quizStatus.attemptCount > 0 && (
              <div className="previous-attempts">
                <h3>Previous Attempts</h3>
                <p>Attempts: {quizStatus.attemptCount}</p>
                {quizStatus.bestScore && <p>Best Score: {quizStatus.bestScore}%</p>}
              </div>
            )}
            
            <button className="btn btn-primary btn-large" onClick={startQuiz}>
              {quizStatus.attemptCount > 0 ? 'Start New Attempt' : 'Start Quiz'}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Show quiz questions
  const answeredCount = Object.keys(answers).length;
  const totalQuestions = attempt.questions.length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <StudentExerciseProtection mode="quiz">
      <div className="quiz-page">
        <div className="quiz-header">
          <h1>Quiz - Attempt {attempt.attemptNumber}</h1>
          <div className="quiz-progress">
            {answeredCount} / {totalQuestions} answered
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="questions-container">
          {attempt.questions.map((question, index) => (
            <div key={question.id} className="question-card">
              <div className="question-number">Question {index + 1}</div>
              <div className="question-text">{question.question}</div>

              {question.type === 'multiple-choice' && (
                <div className="options-list">
                  {question.options.map((option, optIndex) => (
                    <label key={optIndex} className="option-label">
                      <input
                        type="radio"
                        name={question.id}
                        checked={answers[question.id] === optIndex}
                        onChange={() => handleAnswer(question.id, optIndex)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.type === 'fill-in-blank' && (
                <input
                  type="text"
                  className="fill-blank-input"
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswer(question.id, e.target.value)}
                  placeholder="Your answer"
                />
              )}

              {question.type === 'code-reading' && (
                <div className="options-list">
                  {question.options.map((option, optIndex) => (
                    <label key={optIndex} className="option-label">
                      <input
                        type="radio"
                        name={question.id}
                        checked={answers[question.id] === optIndex}
                        onChange={() => handleAnswer(question.id, optIndex)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="quiz-actions">
          <button 
            className="btn btn-success btn-large" 
            onClick={() => setShowSubmitConfirmation(true)}
            disabled={submitting || answeredCount < totalQuestions}
          >
            Submit Quiz
          </button>
          {answeredCount < totalQuestions && (
            <p className="warning-text">Please answer all questions before submitting</p>
          )}
        </div>

        {showSubmitConfirmation && (
          <div
            className="submit-confirmation-backdrop"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !submitting) {
                setShowSubmitConfirmation(false);
              }
            }}
          >
            <section
              className="submit-confirmation-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="submit-confirmation-title"
              aria-describedby="submit-confirmation-description"
              onKeyDown={(event) => {
                if (event.key === 'Escape' && !submitting) {
                  setShowSubmitConfirmation(false);
                }
              }}
            >
              <div className="confirmation-icon" aria-hidden="true">?</div>
              <h2 id="submit-confirmation-title">Submit your quiz?</h2>
              <p id="submit-confirmation-description">
                You answered {answeredCount} of {totalQuestions} questions.
                After submitting, you won’t be able to change your answers.
              </p>
              {error && <div className="error-message">{error}</div>}
              <div className="confirmation-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSubmitConfirmation(false)}
                  disabled={submitting}
                  autoFocus
                >
                  Review answers
                </button>
                <button
                  className="btn btn-success"
                  onClick={confirmSubmitQuiz}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting…' : 'Yes, submit quiz'}
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
      </StudentExerciseProtection>
    </Layout>
  );
}

export default QuizPage;
